import { ReactNode, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useGetUserQuery } from '~/api/v2/hooks';
import { useAppDispatch, useAppSelector } from '~/redux';
import { selectAccessToken, selectAuthError } from '~/redux/slices/authSlice';
import {
  clearCurrentUser,
  selectCurrentUser,
  setCurrentUser,
  setCurrentUserLoading,
} from '~/redux/slices/currentUserSlice';
import { selectIsSessionInitialized } from '~/redux/slices/userSessionSlice';
import AuthErrorModal from './AuthErrorModal';

const AuthChecker = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const isSessionInitialized = useAppSelector(selectIsSessionInitialized);
  const accessToken = useAppSelector(selectAccessToken);
  const currentUser = useAppSelector(selectCurrentUser);
  const authError = useAppSelector(selectAuthError);
  const { data: user, error, isFetching } = useGetUserQuery({}, { skip: !isSessionInitialized || !accessToken });

  useEffect(() => {
    if (user && user !== currentUser) {
      dispatch(setCurrentUser(user));
    }
  }, [user, currentUser, dispatch]);

  useEffect(() => {
    dispatch(setCurrentUserLoading(isFetching));
  }, [isFetching, dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(clearCurrentUser());
    }
  }, [error, dispatch]);

  if (!accessToken && isSessionInitialized) {
    return <Navigate to="/logout" replace />;
  }

  // Any 4xx captured by the base query is surfaced here as a blocking modal
  if (authError) {
    return <AuthErrorModal message={authError.message} status={authError.status} />;
  }

  return <>{children}</>;
};

export default AuthChecker;
